import { useState, useRef, useEffect } from "react";
import { format, parse, isValid } from "date-fns";

const PRESETS = [
  { label: "1h",  ms: 1  * 3600_000 },
  { label: "12h", ms: 12 * 3600_000 },
  { label: "24h", ms: 24 * 3600_000 },
  { label: "7d",  ms: 7  * 86400_000 },
  { label: "30d", ms: 30 * 86400_000 },
];

const REFRESH_OPTIONS = [
  { label: "Off",   ms: 0 },
  { label: "Auto",  ms: 5 * 60_000 },
  { label: "5s",    ms: 5_000 },
  { label: "10s",   ms: 10_000 },
  { label: "30s",   ms: 30_000 },
  { label: "1m",    ms: 60_000 },
  { label: "5m",    ms: 5  * 60_000 },
  { label: "15m",   ms: 15 * 60_000 },
  { label: "30m",   ms: 30 * 60_000 },
  { label: "1h",    ms: 3600_000 },
  { label: "2h",    ms: 2  * 3600_000 },
  { label: "1d",    ms: 86400_000 },
];

// Parse a Grafana-style shorthand like "now-1h", "now-2d", "now-30m", "now-7d"
// into { start, end } ms timestamps. Returns null if not recognized.
function parseShorthand(raw) {
  const str = raw.trim().toLowerCase();
  if (str === "now") {
    const end = Date.now();
    return { start: end - 3600_000, end };
  }
  const m = str.match(/^now-(\d+)(m|h|d|w)$/);
  if (!m) return null;
  const [, n, unit] = m;
  const amount = parseInt(n, 10);
  const end = Date.now();
  let ms;
  if (unit === "m") ms = amount * 60_000;
  else if (unit === "h") ms = amount * 3600_000;
  else if (unit === "d") ms = amount * 86400_000;
  else if (unit === "w") ms = amount * 7 * 86400_000;
  return { start: end - ms, end };
}

// Parse a "YYYY-MM-DD to YYYY-MM-DD" range string.
function parseDateRange(raw) {
  const parts = raw.split(/\s+to\s+/i);
  if (parts.length !== 2) return null;
  const fmt = "yyyy-MM-dd";
  const start = parse(parts[0].trim(), fmt, new Date());
  const end   = parse(parts[1].trim(), fmt, new Date());
  if (!isValid(start) || !isValid(end)) return null;
  end.setHours(23, 59, 59, 999);
  if (start > end) return null;
  return { start: start.getTime(), end: end.getTime() };
}

function parseCustomInput(raw) {
  return parseShorthand(raw) ?? parseDateRange(raw);
}

function formatRange(start, end) {
  const now = Date.now();
  if (Math.abs(end - now) < 5 * 60_000) {
    const diff = now - start;
    const mins = Math.round(diff / 60_000);
    if (mins % (7 * 24 * 60) === 0) return `now-${mins / (7 * 24 * 60)}w`;
    if (mins % (24 * 60) === 0) return `now-${mins / (24 * 60)}d`;
    if (mins % 60 === 0) return `now-${mins / 60}h`;
    return `now-${mins}m`;
  }
  return `${format(new Date(start), "yyyy-MM-dd")} to ${format(new Date(end), "yyyy-MM-dd")}`;
}

export default function TimeRangePicker({ start, end, onRangeChange, dark = false }) {
  const [showCustom, setShowCustom] = useState(false);
  const [showRefresh, setShowRefresh] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState(null);
  const [refreshMs, setRefreshMs] = useState(0); // 0 = off
  const [countdown, setCountdown] = useState(0);

  const popoverRef = useRef(null);
  const refreshRef = useRef(null);
  const inputRef = useRef(null);
  // Keep a stable ref to the current range duration so the interval can slide it
  const rangeDurationRef = useRef(end - start);
  useEffect(() => { rangeDurationRef.current = end - start; }, [start, end]);

  // Which preset is currently active (null if custom range)
  const activePreset = PRESETS.find((p) => {
    const expectedStart = end - p.ms;
    return Math.abs(start - expectedStart) < 5_000 && Math.abs(end - Date.now()) < 5 * 60_000;
  });

  // Auto-refresh interval — slides the window forward on each tick
  const onRangeChangeRef = useRef(onRangeChange);
  useEffect(() => { onRangeChangeRef.current = onRangeChange; }, [onRangeChange]);

  useEffect(() => {
    if (refreshMs === 0) {
      setCountdown(0);
      return;
    }
    setCountdown(refreshMs / 1000);
    const tick = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          const now = Date.now();
          onRangeChangeRef.current({ start: now - rangeDurationRef.current, end: now });
          return refreshMs / 1000;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [refreshMs]);

  // Close custom popover on outside click
  useEffect(() => {
    if (!showCustom) return;
    function handler(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setShowCustom(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCustom]);

  // Close refresh popover on outside click
  useEffect(() => {
    if (!showRefresh) return;
    function handler(e) {
      if (refreshRef.current && !refreshRef.current.contains(e.target)) {
        setShowRefresh(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showRefresh]);

  function openCustom() {
    setInputValue(formatRange(start, end));
    setInputError(null);
    setShowCustom(true);
    setShowRefresh(false);
    setTimeout(() => inputRef.current?.select(), 0);
  }

  function applyCustom() {
    const result = parseCustomInput(inputValue);
    if (!result) {
      setInputError("Use now-1h, now-7d, now-30m, or YYYY-MM-DD to YYYY-MM-DD");
      return;
    }
    onRangeChange(result);
    setShowCustom(false);
    setInputError(null);
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") applyCustom();
    if (e.key === "Escape") setShowCustom(false);
  }

  function selectPreset(preset) {
    const now = Date.now();
    onRangeChange({ start: now - preset.ms, end: now });
    setShowCustom(false);
  }

  function selectRefresh(option) {
    setRefreshMs(option.ms);
    setShowRefresh(false);
  }

  const activeRefresh = REFRESH_OPTIONS.find((o) => o.ms === refreshMs);
  const isRefreshing = refreshMs > 0;

  const btn = dark
    ? "bg-gray-700 text-gray-300 hover:bg-gray-600 border-gray-600"
    : "bg-white text-gray-600 hover:bg-gray-50 border-gray-200";
  const btnActive = dark
    ? "bg-indigo-600 text-white border-indigo-600"
    : "bg-cyan-600 text-white border-cyan-600";
  const btnRefreshActive = dark
    ? "bg-emerald-700 text-white border-emerald-700"
    : "bg-emerald-600 text-white border-emerald-600";

  return (
    <div className="relative flex items-center gap-1">
      {/* Preset buttons */}
      <div className={`flex rounded overflow-hidden border ${dark ? "border-gray-600" : "border-gray-200"}`}>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            onClick={() => selectPreset(p)}
            className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
              activePreset?.label === p.label ? btnActive : btn
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom button */}
      <button
        onClick={openCustom}
        className={`px-2.5 py-1.5 text-xs font-medium rounded border transition-colors ${
          !activePreset && !showCustom ? btnActive : btn
        }`}
      >
        {!activePreset ? formatRange(start, end) : "Custom"}
      </button>

      {/* Refresh button */}
      <div className="relative" ref={refreshRef}>
        <button
          onClick={() => { setShowRefresh((v) => !v); setShowCustom(false); }}
          title="Auto-refresh interval"
          className={`px-2.5 py-1.5 text-xs font-medium rounded border transition-colors flex items-center gap-1 ${
            isRefreshing ? btnRefreshActive : btn
          }`}
        >
          {/* Circular refresh icon */}
          <svg
            className={`w-3 h-3 ${isRefreshing ? "animate-spin" : ""}`}
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M13.5 8a5.5 5.5 0 1 1-1.5-3.8" />
            <polyline points="13 2 13.5 5.5 10 5.5" />
          </svg>
          {isRefreshing ? `${activeRefresh.label} (${countdown}s)` : activeRefresh?.label ?? "Off"}
        </button>

        {showRefresh && (
          <div className={`absolute top-full right-0 mt-1 z-50 border rounded-xl shadow-lg p-2 w-36 ${
            dark ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"
          }`}>
            <div className={`text-xs font-semibold uppercase tracking-wide mb-2 px-1 ${dark ? "text-gray-400" : "text-gray-500"}`}>
              Refresh
            </div>
            {REFRESH_OPTIONS.map((o) => (
              <button
                key={o.label}
                onClick={() => selectRefresh(o)}
                className={`w-full text-left text-xs px-2 py-1.5 rounded transition-colors ${
                  refreshMs === o.ms
                    ? dark ? "bg-emerald-700 text-white" : "bg-emerald-50 text-emerald-700 font-medium"
                    : dark ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Custom popover */}
      {showCustom && (
        <div
          ref={popoverRef}
          className="absolute top-full right-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-80"
        >
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Custom Range</div>

          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => { setInputValue(e.target.value); setInputError(null); }}
            onKeyDown={handleKeyDown}
            placeholder="now-1h or 2025-05-01 to 2025-05-07"
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent font-mono"
          />
          {inputError && (
            <p className="text-xs text-red-500 mt-1">{inputError}</p>
          )}

          <div className="mt-3 mb-3">
            <div className="text-xs text-gray-400 mb-2">Quick shorthands</div>
            <div className="flex flex-wrap gap-1.5">
              {["now-30m","now-1h","now-6h","now-12h","now-24h","now-2d","now-7d","now-14d","now-30d"].map((s) => (
                <button
                  key={s}
                  onClick={() => { setInputValue(s); setInputError(null); }}
                  className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 font-mono transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="text-xs text-gray-400 mb-3">
            Or enter a date range: <span className="font-mono">YYYY-MM-DD to YYYY-MM-DD</span>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowCustom(false)}
              className="text-xs text-gray-400 hover:text-gray-600 px-3 py-1.5"
            >
              Cancel
            </button>
            <button
              onClick={applyCustom}
              className="text-xs bg-cyan-600 text-white rounded-lg px-4 py-1.5 hover:bg-cyan-700 transition-colors font-medium"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
